import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAmplitudeExperiment } from '../utils/ampli';
import config from 'config';
import { selectUser } from '../reducers/user.selectors';
import { useSelector, useStore } from 'react-redux';
import { useTriggeredHint } from '../utils/useTriggeredHint';
import useRouter from '../utils/useRouter';
import services from '../services';
import { onboardingHints, routes } from '../constants';
import { StoreType } from '../reducers';
import { getMindItem } from '../reducers/mindItems.selectors';
import {
  getAiGeneratedImagesRemainingLimit,
  getCurrentConversation,
  getCurrentConversationId,
  getGeneratedPodcastId,
  getLastGeneratedImageUrl,
  getLastGeneratedArtifactId,
  getPromptsAvailableAgainAt,
  getPromptsRemainingLimit,
  getTemporaryAttachments,
  getTemporaryConversation,
} from '../reducers/aiAssistant.selectors';
import {
  ABORT_REASON,
  createNewConversation as createNewConversationAction,
  streamConversationResponse as streamConversationResponseAction,
  addNewMessage as addNewMessageAction,
  removeLastAssistantResponse as removeLastAssistantResponseAction,
  setCurrentConversationId as setCurrentConversationIdAction,
  clearActiveDocument as clearActiveDocumentAction,
  getConversation as getConversationAction,
  syncConversationFromServer as syncConversationFromServerAction,
  restoreContextAttachments as restoreContextAttachmentsAction,
  saveContextAttachments as saveContextAttachmentsAction,
  clearContextAttachments as clearContextAttachmentsAction,
  clearTemporaryAttachments as clearTemporaryAttachmentsAction,
  clearLastGeneratedImageUrl as clearLastGeneratedImageUrlAction,
  clearLastGeneratedArtifactId as clearLastGeneratedArtifactIdAction,
  getPromptsRemainingLimit as getPromptsRemainingLimitAction,
} from '../actions/aiAssistant.actions';
import useAction from '../utils/useAction';
import { isRateLimited } from '../utils/isRateLimited';
import { v4 } from 'uuid';
import { ConversationNewMessage } from '../reducers/aiAssistant.reducers';
import {
  ConversationMessageContext,
  TemplatePrompts
} from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import { ActiveView } from '../components/AIAssistant/shared/constants';
import { notifyBugsnagHandledError, notifyBugsnagNetwork } from '../utils/bugsnagClient';
import { GeneratedPodcastStageStatus } from '@klab-berlin/api-sdk/lib/types/responses/Ai';
import useMukiBudeOnboarding,
{ MukiBudeOnboardingState, MukiBudeOnboardingStep } from '../components/AIAssistant/Dialog/hooks/useMukiBudeOnboarding';
import { useReaderContext } from '../components/Reader/ReaderContext';
import { getAIAssistantPageSource } from '../utils/getAIAssistantPageSource';

export enum AIAssistantDisplayMode {
  DIALOG = 'DIALOG',
  PAGE = 'PAGE',
}

interface AIAssistantContext {
  isAssistantDragging: boolean;
  setIsAssistantDragging: (isAssistantDragging: boolean) => void;
  isAssistantOpen: boolean;
  openAssistant: (mindItemId?: string, conversationId?: string) => Promise<void>;
  closeAssistant: VoidFunction;
  assistantPosition: {x: number, y: number};
  setAssistantPosition: (position: {x: number, y: number}) => void;
  draggableBounds: {left: number, right: number, top: number, bottom: number};
  setDraggableBounds: (bounds: {left: number, right: number, top: number, bottom: number}) => void;
  isExtended: boolean
  toggleExtended: VoidFunction,
  setIsExtended: (isExtended: boolean) => void,
  dialogContentRef: React.RefObject<HTMLDivElement> | null
  dialogFooterRef: React.RefObject<HTMLDivElement> | null
  lastUserMessageRef: React.RefObject<HTMLDivElement> | null
  dialogMessagesContainerRef: React.RefObject<HTMLDivElement> | null
  isUserPromptLimitReached: boolean,
  isPageLimitReached: boolean,
  isMessagesRateLimitActive: boolean,
  isAttachmentsRateLimitActive: boolean,
  isPromptLimitReached: boolean,
  promptLimitAlertVisible: boolean,
  setPromptLimitAlertVisible: (visible: boolean) => void,
  showPromptLimitAlert: () => void,
  attachmentUploadError: string | null,
  setAttachmentUploadError: (error: string | null) => void,
  attachmentInfoMessage: string | null,
  setAttachmentInfoMessage: (message: string | null) => void,
  imageUploadError: string | null,
  setImageUploadError: (error: string | null) => void,
  documentDownloadAlertVisible: boolean,
  setDocumentDownloadAlertVisible: (visible: boolean) => void,
  imageGenerationLimitReachedAlertVisible: boolean,
  setImageGenerationLimitReachedAlertVisible: (visible: boolean) => void,
  isArtifactGenerationOnboardingAlertVisible: boolean,
  setIsArtifactGenerationOnboardingAlertVisible: (visible: boolean) => void,
  isAssistantDisabled: boolean;
  wasFeedbackOpen: boolean,
  setWasFeedbackOpen: (payload: boolean) => void,
  activeView: ActiveView
  setActiveView: (view: ActiveView) => void
  selectedTemplatePromptId: TemplatePrompts | null
  setSelectedTemplatePromptId: (id: TemplatePrompts | null) => void
  processNewConversation: ({
    message,
    messageContext,
    activeViewOverride,
  }: {
    message: string,
    messageContext?: ConversationMessageContext,
    activeViewOverride?: ActiveView,
  }) => void
  stopResponse: () => void,
  continueConversation: ({ 
    message,
    messageContext
  }: { 
    message: string, 
    messageContext?: ConversationMessageContext 
  }) => void
  regenerateResponse: () => void,
  resetActiveView: () => void,
  onboardingState: MukiBudeOnboardingState | null,
  stagesShownInCurrentSession: Set<MukiBudeOnboardingStep>,
  concludeOnboardingStep: (step: MukiBudeOnboardingStep) => void
  currentSessionHints: Set<string>,
  addCurrentSessionHint: (hint: string) => void
  aiFileUploadAbortControllerRef: React.MutableRefObject<AbortController | null> | null,
  displayMode: AIAssistantDisplayMode,
  readyPodcastId: string | null,
  pendingPodcastDetails: boolean,
  clearPendingPodcastDetails: () => void,
  preparePodcastNavigation: (isGenerating: boolean, podcastId: string) => void,
  pendingPodcastIdRef: React.MutableRefObject<string | null>,
  isPodcastV1UxEnabled: boolean,
}

export const aiAssistantContext = createContext<AIAssistantContext>({
  isAssistantDragging: false,
  setIsAssistantDragging: () => {},
  isAssistantOpen: false,
  openAssistant: () => Promise.resolve(),
  closeAssistant: () => {},
  assistantPosition: { x: 0, y: 0 },
  setAssistantPosition: () => {},
  draggableBounds: { left: 0, right: 0, top: 0, bottom: 0 },
  setDraggableBounds: () => {},
  isExtended: false,
  toggleExtended: () => false,
  setIsExtended: () => {},
  dialogContentRef: null,
  dialogFooterRef: null,
  lastUserMessageRef: null,
  dialogMessagesContainerRef: null,
  isUserPromptLimitReached: false,
  isPageLimitReached: false,
  isMessagesRateLimitActive: false,
  isAttachmentsRateLimitActive: false,
  isPromptLimitReached: false,
  promptLimitAlertVisible: false,
  setPromptLimitAlertVisible: () => {},
  showPromptLimitAlert: () => {},
  attachmentUploadError: null,
  setAttachmentUploadError: () => {},
  attachmentInfoMessage: null,
  setAttachmentInfoMessage: () => {},
  imageUploadError: null,
  setImageUploadError: () => {},
  documentDownloadAlertVisible: false,
  setDocumentDownloadAlertVisible: () => {},
  imageGenerationLimitReachedAlertVisible: false,
  setImageGenerationLimitReachedAlertVisible: () => {},
  isArtifactGenerationOnboardingAlertVisible: false,
  setIsArtifactGenerationOnboardingAlertVisible: () => {},
  isAssistantDisabled: false,
  wasFeedbackOpen: false,
  setWasFeedbackOpen: () => {},
  activeView: ActiveView.CHAT,
  setActiveView: () => {},
  selectedTemplatePromptId: null,
  setSelectedTemplatePromptId: () => {},
  processNewConversation: () => {},
  stopResponse: () => {},
  continueConversation: () => {},
  regenerateResponse: () => {},
  resetActiveView: () => {},
  onboardingState: null,
  concludeOnboardingStep: () => {},
  stagesShownInCurrentSession: new Set(),
  currentSessionHints: new Set(),
  addCurrentSessionHint: () => {},
  aiFileUploadAbortControllerRef: null,
  displayMode: AIAssistantDisplayMode.DIALOG,
  readyPodcastId: null,
  pendingPodcastDetails: false,
  clearPendingPodcastDetails: () => {},
  preparePodcastNavigation: () => {},
  pendingPodcastIdRef: { current: null },
  isPodcastV1UxEnabled: false,
});

const AIAssistantProvider: React.FC = ({ children }) => {
  const user = useSelector(selectUser);
  const { hasSeen: hasSeenArtifactAlert, record: recordArtifactAlertSeen } =
    useTriggeredHint(onboardingHints.aiAssistant.artifactGenerationOnboardingAlertSeen);
  const { match: { location: { pathname }, params }, router } = useRouter();
  const store = useStore<StoreType>();

  const [isAssistantDragging, setIsAssistantDragging] = useState(false);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [assistantPosition, setAssistantPosition] = useState({ x: 0, y: 0 });
  const [draggableBounds, setDraggableBounds] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  const [isExtended, setIsExtended] = useState(false);
  const { mindItemId, currentPage, selectedPagesForAI } = useReaderContext();
  const { onboardingState, concludeOnboardingStep, stagesShownInCurrentSession } = useMukiBudeOnboarding();
  const [displayMode, setDisplayMode] = useState<AIAssistantDisplayMode>(AIAssistantDisplayMode.DIALOG);

  // Local refs
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const dialogFooterRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const dialogMessagesContainerRef = useRef<HTMLDivElement>(null);
  const lastVisitedPathRef = useRef(pathname);
  const previousPathForConversationIdSyncRef = useRef(pathname);
  const abortControllerRef = useRef<AbortController | null>(null);
  const aiFileUploadAbortControllerRef = useRef<AbortController | null>(null);
  const previousContextKeyRef = useRef<string | null>(null);
  const isSendingMessageRef = useRef<boolean>(false);
  const isPodcastLoadingViewRef = useRef(false);
  // set only when the poll settles; blocks the intercept from bouncing back to the loading screen
  const podcastSettledConversationIdRef = useRef<string | null>(null);
  const [isPodcastPollDone, setIsPodcastPollDone] = useState(false);
  const [readyPodcastId, setReadyPodcastId] = useState<string | null>(null);
  const [pendingPodcastDetails, setPendingPodcastDetails] = useState(false);
  // Carries the clicked podcast id; cached conversation messages can predate it.
  const pendingPodcastIdRef = useRef<string | null>(null);
  const clearPendingPodcastDetails = useCallback(() => {
    setPendingPodcastDetails(false);
    pendingPodcastIdRef.current = null;
  }, []);

  // Called by Meine Inhalte cards before navigating, so the AI Tools page
  // mounts straight into the right view instead of flashing the previous one.
  const preparePodcastNavigation = useCallback((isGenerating: boolean, podcastId: string) => {
    pendingPodcastIdRef.current = podcastId;
    if (isGenerating) {
      isPodcastLoadingViewRef.current = true;
      setActiveView(ActiveView.PODCAST_LOADING);
    } else {
      setPendingPodcastDetails(true);
      setActiveView(ActiveView.CHAT);
    }
  }, []);

  const abortCurrentStream = useCallback((reason: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(reason);
      abortControllerRef.current = null;
    }
  }, []);

  // Amplitude experiments and session state
  const [wasFeedbackOpen, setWasFeedbackOpen] = useState(false);
  const [isAssistantDisabled, setIsAssistantDisabled] = useState(false);
  const [activeView, setActiveView] = useState<ActiveView>(ActiveView.FREE_DRAFT_ENTRY);
  const [selectedTemplatePromptId, setSelectedTemplatePromptId] = useState<TemplatePrompts | null>(null);

  const {
    isReady: isDisableFeatureReady,
    variant: disableFeatureVariant
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.disableAiAssistantButton);

  // Resolved once here and shared via context so the router (AIToolsPage) and the
  // mounted chat shell (AIAssistantChat) gate on the SAME readiness/variant — a
  // per-component hook would let the freshly-mounted shell read a not-yet-ready
  // 'control' on the first PODCAST_LOADING render.
  const {
    isReady: isPodcastV1UxReady,
    variant: podcastV1UxVariant
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.enablePodcastV1Ux);
  const isPodcastV1UxEnabled = isPodcastV1UxReady && podcastV1UxVariant === 'on';

  // Conversation state
  const currentConversationId = useSelector(getCurrentConversationId);
  const setCurrentConversationId = useAction(setCurrentConversationIdAction);
  const currentConversation = useSelector(getCurrentConversation);
  const generatedPodcastIdFromStream = useSelector(getGeneratedPodcastId);
  const generatedPodcastIdFromMessages = useMemo(() => {
    const messages = currentConversation?.data?.messages;
    if (!messages) return null;
    const podcastMessage = messages.find(
      (msg) => msg.role === 'assistant' && (msg.context as any)?.generatedPodcastId
    );
    return (podcastMessage?.context as any)?.generatedPodcastId as string | null ?? null;
  }, [currentConversation?.data?.messages]);
  const generatedPodcastId = generatedPodcastIdFromStream || generatedPodcastIdFromMessages;
  const createNewConversation = useAction(createNewConversationAction);
  const addNewMessage = useAction(addNewMessageAction);
  const saveContextAttachments = useAction(saveContextAttachmentsAction);
  const restoreContextAttachments = useAction(restoreContextAttachmentsAction);
  const clearContextAttachments = useAction(clearContextAttachmentsAction);
  const clearTemporaryAttachments = useAction(clearTemporaryAttachmentsAction);
  const temporaryAttachments = useSelector(getTemporaryAttachments);
  const streamConversationResponse = useAction(streamConversationResponseAction);
  const removeLastAssistantResponse = useAction(removeLastAssistantResponseAction);
  const clearActiveDocument = useAction(clearActiveDocumentAction);
  const getConversation = useAction(getConversationAction);
  const syncConversationFromServer = useAction(syncConversationFromServerAction);
  // const activeDocumentData = useSelector(getActiveDocumentData);
  const userPromptCount = currentConversation?.data?.messages.filter(({ role }) => role === 'user').length ?? 0;
  const isUserPromptLimitReached = userPromptCount >= config.ai.readerChat.maxUserPromptCount;
  const pageCount = currentConversation?.data?.context?.mindItem?.pages?.length ?? 0;
  const isPageLimitReached = pageCount >= config.ai.readerChat.maxDocumentPagesCount;
  const messagesRateLimitExpiresAt = currentConversation?.data?.rateLimit?.messages?.expiresAt ?? null;
  const attachmentsRateLimitExpiresAt = currentConversation?.data?.rateLimit?.attachments?.expiresAt ?? null;
  const isMessagesRateLimitActive = isRateLimited(messagesRateLimitExpiresAt);
  const isAttachmentsRateLimitActive = isRateLimited(attachmentsRateLimitExpiresAt);
  const promptsRemainingLimit = useSelector(getPromptsRemainingLimit);
  const isPromptLimitReached = promptsRemainingLimit === 0;
  const fetchPromptsRemainingLimit = useAction(getPromptsRemainingLimitAction);
  const [promptLimitAlertVisible, setPromptLimitAlertVisible] = useState(false);
  const prevPromptsRemainingLimitRef = useRef(promptsRemainingLimit);
  const showPromptLimitAlert = useCallback(() => {
    if (isPromptLimitReached) {
      setPromptLimitAlertVisible(true);
    }
  }, [isPromptLimitReached]);

  // Show alert only when remaining transitions from >0 to 0 (prompt #100)
  useEffect(() => {
    const prev = prevPromptsRemainingLimitRef.current;
    prevPromptsRemainingLimitRef.current = promptsRemainingLimit;
    if (prev !== null && prev > 0 && promptsRemainingLimit === 0) {
      setPromptLimitAlertVisible(true);
    } else if (prev === 0 && promptsRemainingLimit !== null && promptsRemainingLimit > 0) {
      setPromptLimitAlertVisible(false);
    }
  }, [promptsRemainingLimit]);
  const availableAgainAt = useSelector(getPromptsAvailableAgainAt);
  useEffect(() => {
    if (!availableAgainAt) return;
    const delay = availableAgainAt - Date.now();
    if (delay <= 0) {
      fetchPromptsRemainingLimit();
      return;
    }
    const timer = setTimeout(() => fetchPromptsRemainingLimit(), delay);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableAgainAt]);

  const [attachmentUploadError, setAttachmentUploadError] = useState<string | null>(null);
  const [attachmentInfoMessage, setAttachmentInfoMessage] = useState<string | null>(null);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [documentDownloadAlertVisible, setDocumentDownloadAlertVisible] = useState(false);
  const [imageGenerationLimitReachedAlertVisible, setImageGenerationLimitReachedAlertVisible] =
    useState(false);
  const [isArtifactGenerationOnboardingAlertVisible, setIsArtifactGenerationOnboardingAlertVisible] = useState(false);
  const clearLastGeneratedImageUrl = useAction(clearLastGeneratedImageUrlAction);
  const clearLastGeneratedArtifactId = useAction(clearLastGeneratedArtifactIdAction);
  const [currentSessionHints, setCurrentSessionHints] = useState<Set<string>>(new Set());
  const prevDisplayModeRef = useRef<AIAssistantDisplayMode>(displayMode);
  
  const isReaderPage = pathname.startsWith(`/${routes.reader.root}/`);

  useEffect(() => {
    fetchPromptsRemainingLimit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReaderPage) {
      setDisplayMode(AIAssistantDisplayMode.DIALOG);
    } else {
      setDisplayMode(AIAssistantDisplayMode.PAGE);
    }
  }, [pathname]);

  // Clean up conversation state when switching between display modes
  useEffect(() => {
    const prevMode = prevDisplayModeRef.current;
    const currentMode = displayMode;

    if (prevMode !== currentMode) {
      const isAIToolsPage = pathname.startsWith(`/${routes.aiTools.root}`);
      const urlConversationId = (params as { conversationId?: string })?.conversationId;
      const shouldPreserveConversation = isAIToolsPage && !!urlConversationId;

      // Only clear if assistant is closed to avoid disrupting active sessions
      if (!isAssistantOpen && !shouldPreserveConversation) {
        setCurrentConversationId({ conversationId: null });
      }
    }

    prevDisplayModeRef.current = currentMode;
  }, [displayMode, isAssistantOpen, pathname, params, setCurrentConversationId]);

  // Get conversations data for memoization
  const conversationsById = useSelector((state: StoreType) => state.aiAssistant.conversations.byId);
  const temporaryConversation = useSelector(getTemporaryConversation);
  
  // Memoized function to find conversation by mindItemId
  const findConversationForMindItem = useCallback((mindItemId: string | undefined): string | null => {
    if (!mindItemId) return null;
    
    // Check temporary conversation first
    if (temporaryConversation?.data?.context?.mindItem?.id === mindItemId) {
      return temporaryConversation.data.id;
    }
    
    // Search through conversations
    for (const [conversationId, conversationState] of Object.entries(conversationsById)) {
      const conversation = conversationState?.data;
      if (conversation?.context?.mindItem?.id === mindItemId) {
        return conversationId;
      }
    }
    
    return null;
  }, [conversationsById, temporaryConversation]);
  
  const openAssistant: AIAssistantContext['openAssistant'] = async (mindItemId?: string, conversationId?: string) => {
    setIsAssistantOpen(true);
    const mindItem = mindItemId ? getMindItem(store.getState(), mindItemId)?.data : undefined;
    const isViewingDocumentMindItem = mindItem?.fileType === 'document';
    services.track.ampliTrackAIAssistantOpenedEvent({ mindItem, pageSource: getAIAssistantPageSource(pathname) });
    if (conversationId) {
      try {
        await getConversation({ conversationId });
        setCurrentConversationId({ conversationId });
        setActiveView(ActiveView.CHAT);
      } catch (error) {
        notifyBugsnagNetwork('AIAssistant', 'openAssistant', { conversationId, mindItemId, error });
      }
      return;
    }
    
    if (isReaderPage && mindItemId) {
      const existingConversationId = findConversationForMindItem(mindItemId);
      
      if (existingConversationId) {
        const currentState = store.getState();
        const conversationExists = currentState.aiAssistant.conversations.byId[existingConversationId] ||
          currentState.aiAssistant.conversations.temporaryConversation?.data?.id === existingConversationId;
        
        if (conversationExists) {
          setCurrentConversationId({ conversationId: existingConversationId });
          setActiveView(ActiveView.CHAT);
        } else {
          setActiveView(ActiveView.UNIT_ENTRY);
        }
      } else {
        setActiveView(ActiveView.UNIT_ENTRY);
      }
    } else if (isViewingDocumentMindItem) {
      setActiveView(ActiveView.UNIT_ENTRY);
    } else {
      setActiveView(ActiveView.FREE_DRAFT_ENTRY);
    }
  };

  const closeAssistant = () => {
    setIsAssistantOpen(false);
    setCurrentConversationId({ conversationId: null });
    clearActiveDocument();
  };

  useEffect(() => {
    const isAIToolsPage = pathname.startsWith(`/${routes.aiTools.root}`);
    if (!isAIToolsPage) {
      setPendingPodcastDetails(false);
      pendingPodcastIdRef.current = null;
      return;
    }

    // Get conversationId from URL params (authoritative source for AI Tools page)
    const urlConversationId = (params as { conversationId?: string })?.conversationId;
    const previousPathname = previousPathForConversationIdSyncRef.current;
    const isUserNavigatingFromOtherPage = pathname !== previousPathname;

    // If the URL has a conversation ID
    // and it differs from Redux then update Redux conversation ID from URL.
    if (urlConversationId) {
      if (urlConversationId !== currentConversationId) {
        setCurrentConversationId({ conversationId: urlConversationId });
        getConversation({ conversationId: urlConversationId });
      }
    }
    // If the URL **does not** have a conversation ID
    // and Redux has a conversation ID
    else if (currentConversationId) {
      if (isUserNavigatingFromOtherPage) {
        // and the user is navigating from other page, then clear the Redux conversation ID.
        setCurrentConversationId({ conversationId: null });
      } else {
        // If user is not navigating from other page, push the Redux conversation ID to the URL.
        router.replace(`/${routes.aiTools.root}/${currentConversationId}`);
      }
    }

    previousPathForConversationIdSyncRef.current = pathname;

    // Skip view switching while podcast is generating — the transition
    // effect handles moving to CHAT once the conversation is ready.
    if (isPodcastLoadingViewRef.current === true) {
      return;
    } else if (urlConversationId || currentConversationId) {
      setActiveView(ActiveView.CHAT);
    } else {
      setActiveView(ActiveView.FREE_DRAFT_ENTRY);
    }
  }, [currentConversationId, pathname, params, router]);

  const resetActiveView = () => {
    setActiveView(ActiveView.FREE_DRAFT_ENTRY);
  };

  const processNewConversation: AIAssistantContext['processNewConversation'] = async ({
    message, messageContext, activeViewOverride,
  }) => {
    setReadyPodcastId(null);
    const id = v4();
    const newMessage: ConversationNewMessage = { 
      id,
      role: 'user',
      content: [{ text: message, type: 'text' }],
      context: messageContext
    };

    const context = mindItemId ? {
      mindItem: {
        id: mindItemId,
        pages: selectedPagesForAI.length > 0 ? selectedPagesForAI : [currentPage - 1]
      }
    } : {};
    
    isSendingMessageRef.current = true;
    
    const currentContextKey = `${currentConversationId || 'new'}_${activeView}`;
    clearContextAttachments({ contextKey: currentContextKey });
    clearTemporaryAttachments();

    createNewConversation({
      id,
      message: newMessage,
      context
    });

    isPodcastLoadingViewRef.current = activeViewOverride === ActiveView.PODCAST_LOADING;
    setActiveView(activeViewOverride || ActiveView.CHAT);
    abortCurrentStream(ABORT_REASON.USER_SWITCHED_ACTIVE_VIEW);
    abortControllerRef.current = new AbortController();
    streamConversationResponse({
      conversationId: id,
      abortController: abortControllerRef.current,
    });
  };

  const continueConversation: AIAssistantContext['continueConversation']  = ({ message, messageContext }) => {
    if (!currentConversationId) {
      return;
    }

    isSendingMessageRef.current = true;
    
    const currentContextKey = `${currentConversationId || 'new'}_${activeView}`;
    clearContextAttachments({ contextKey: currentContextKey });
    clearTemporaryAttachments();

    addNewMessage({
      conversationId: currentConversationId,
      message: {
        id: v4(),
        role: 'user',
        content: [{ text: message, type: 'text' }],
        context: messageContext
      }
    });

    abortCurrentStream(ABORT_REASON.USER_SWITCHED_ACTIVE_VIEW);
    abortControllerRef.current = new AbortController();
    streamConversationResponse({
      conversationId: currentConversationId,
      abortController: abortControllerRef.current,
    });
  };

  const regenerateResponse = () => {
    if (!currentConversationId) {
      return;
    }

    removeLastAssistantResponse({
      conversationId: currentConversationId
    });

    abortCurrentStream(ABORT_REASON.USER_SWITCHED_ACTIVE_VIEW);
    abortControllerRef.current = new AbortController();
    streamConversationResponse({
      conversationId: currentConversationId,
      abortController: abortControllerRef.current,
      regenerate: true,
    });
  };

  const stopResponse = () => {
    abortCurrentStream(ABORT_REASON.USER_STOPPED_GENERATING);
  };
  
  const toggleExtended = () => {
    setIsExtended(!isExtended);
  };

  useEffect(() => {
    const isReaderPage = pathname.startsWith(`/${routes.reader.root}/`);
    const prevPath = lastVisitedPathRef.current;
    const prevIsReaderPage = prevPath.startsWith(`/${routes.reader.root}/`);

    // Close only when navigating away from the reader to avoid closing on page->reader transitions.
    if (pathname !== prevPath && prevIsReaderPage && !isReaderPage && isAssistantOpen) {
      closeAssistant();
    }

    if (!isReaderPage) {
      if (activeView === ActiveView.UNIT_ENTRY) {
        setActiveView(ActiveView.FREE_DRAFT_ENTRY);
      }
    }

    lastVisitedPathRef.current = pathname;
  }, [pathname, activeView, isAssistantOpen]);

  // Opening a conversation with a generated podcast lands on the loading screen
  // instead of flashing the chat; its poll then routes ready/errored to CHAT.
  useEffect(() => {
    // The loading view only renders on the KI Tools page; never route to it elsewhere.
    if (!pathname.startsWith(`/${routes.aiTools.root}`)) return;
    if (activeView !== ActiveView.CHAT) return;
    if (pendingPodcastDetails) return;
    if (!currentConversationId || currentConversation?.status !== AsyncActionStatus.READY) return;
    if (podcastSettledConversationIdRef.current === currentConversationId) return;

    const messages = currentConversation?.data?.messages ?? [];
    const hasGeneratedPodcast = messages.some(
      (msg) => msg.role === 'assistant' && (msg.context as any)?.generatedPodcastId
    );
    if (!hasGeneratedPodcast) return;

    isPodcastLoadingViewRef.current = true;
    setActiveView(ActiveView.PODCAST_LOADING);
  }, [activeView, currentConversationId, currentConversation?.status, pendingPodcastDetails, pathname]);

  // Transition from loading screen to chat once the generated podcast poll
  // confirms the podcast is ready (or errored).
  // Also clear the ref if activeView was changed away from PODCAST_LOADING by another code path
  // (e.g. openAssistant, resetActiveView) so the URL sync effect can resume driving activeView.
  useEffect(() => {
    if (activeView === ActiveView.PODCAST_LOADING) {
      if (isPodcastPollDone) {
        podcastSettledConversationIdRef.current = currentConversationId;
        isPodcastLoadingViewRef.current = false;
        setIsPodcastPollDone(false);
        setActiveView(ActiveView.CHAT);
      }
    } else {
      isPodcastLoadingViewRef.current = false;
    }
  }, [activeView, isPodcastPollDone, currentConversationId]);

  // Fetch the conversation from the API to discover the generatedPodcastId,
  // then poll getGeneratedPodcast every 5s until it returns ready or error.
  const PODCAST_POLL_INTERVAL = 5000;
  useEffect(() => {
    if (
      // Poll only while the loading view is actually visible, which requires the KI Tools page.
      !pathname.startsWith(`/${routes.aiTools.root}`) ||
      activeView !== ActiveView.PODCAST_LOADING ||
      !currentConversationId ||
      currentConversation?.status !== AsyncActionStatus.READY
    ) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const start = async () => {
      let podcastId = pendingPodcastIdRef.current || generatedPodcastId;

      if (!podcastId) {
        try {
          const conversation = await services.conversation.getConversation(currentConversationId);
          if (cancelled) return; // effect cleaned up mid-fetch — don't touch the store
          // Sync the store so the assistant message carries generatedPodcastId; the local
          // stream never learns it, and play/pause tracking keys off that message context.
          syncConversationFromServer({ conversationId: currentConversationId, conversation });
          const podcastMessage = conversation.messages.find(
            (msg) => msg.role === 'assistant' && (msg.context as any)?.generatedPodcastId
          );
          podcastId = (podcastMessage?.context as any)?.generatedPodcastId ?? null;
        } catch (error) {
          notifyBugsnagHandledError(error as Error);
          // Settle the poll so the user isn't stranded on the loading screen.
          if (!cancelled) setIsPodcastPollDone(true);
          return;
        }
      }

      if (cancelled) return; // stale run — must not settle the poll for a newer one
      if (!podcastId) {
        // The server conversation has no podcast message either — settle instead
        // of leaving the loading screen up with nothing to poll.
        setIsPodcastPollDone(true);
        return;
      }

      const poll = async () => {
        try {
          const podcast = await services.ai.getGeneratedPodcast(podcastId!);

          if (
            podcast.status === GeneratedPodcastStageStatus.READY ||
            podcast.status === GeneratedPodcastStageStatus.ERROR
          ) {
            if (intervalId) clearInterval(intervalId);
            setReadyPodcastId(podcastId!);
            setIsPodcastPollDone(true);
          }
        } catch (error) {
          notifyBugsnagHandledError(error as Error);
        }
      };

      poll();
      intervalId = setInterval(poll, PODCAST_POLL_INTERVAL);
    };

    start();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeView, currentConversationId, generatedPodcastId, currentConversation?.status, pathname]);

  useEffect(() => {
    const isAssistantDisabled = isDisableFeatureReady && disableFeatureVariant === 'on';
    setIsAssistantDisabled(isAssistantDisabled);
  }, [isDisableFeatureReady, disableFeatureVariant]);

  // Clear document stream and transient states when conversation changes
  const prevConversationIdRef = useRef(currentConversationId);
  
  useEffect(() => {
    const conversationChanged = prevConversationIdRef.current !== currentConversationId;
    
    if (conversationChanged) {
      // readyPodcastId is only meaningful for the conversation that was polling
      setReadyPodcastId(null);
      setSelectedTemplatePromptId(null);
      setAttachmentUploadError(null);
      setAttachmentInfoMessage(null);
      setImageUploadError(null);
      setDocumentDownloadAlertVisible(false);
      setImageGenerationLimitReachedAlertVisible(false);
      setIsArtifactGenerationOnboardingAlertVisible(false);
      clearLastGeneratedImageUrl();
    }
    prevConversationIdRef.current = currentConversationId;
  }, [currentConversationId, clearLastGeneratedImageUrl]);

  const imagesRemainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
  const lastGeneratedImageUrl = useSelector(getLastGeneratedImageUrl);
  const lastGeneratedArtifactId = useSelector(getLastGeneratedArtifactId);

  // Show the limit-reached alert when a generation just completed and the 7-day
  // image quota is now exhausted. lastGeneratedImageUrl (set together with
  // remainingLimit on TOOL_USE_END) is the "just generated this session" signal;
  // consuming it here prevents re-triggering and keeps history/on-mount loads silent.
  useEffect(() => {
    if (lastGeneratedImageUrl && imagesRemainingLimit === 0) {
      setImageGenerationLimitReachedAlertVisible(true);
      clearLastGeneratedImageUrl();
    }
  }, [lastGeneratedImageUrl, imagesRemainingLimit, clearLastGeneratedImageUrl]);

  useEffect(() => {
    if (!lastGeneratedArtifactId) return;
    // Consume the signal before the page check so an artifact generated
    // elsewhere (e.g. Reader) cannot trigger the celebration later on KI Tools
    clearLastGeneratedArtifactId();

    if (!pathname.startsWith(`/${routes.aiTools.root}`)) return;
    if (hasSeenArtifactAlert) return;

    recordArtifactAlertSeen();
    setIsArtifactGenerationOnboardingAlertVisible(true);
  }, [lastGeneratedArtifactId, pathname, clearLastGeneratedArtifactId, hasSeenArtifactAlert, recordArtifactAlertSeen]);

  useEffect(() => {
    const getContextKey = (convId: string | null, view: ActiveView) => {
      return `${convId || 'new'}_${view}`;
    };

    const currentContextKey = getContextKey(currentConversationId, activeView);
    const previousContextKey = previousContextKeyRef.current;
    const wasSendingMessage = isSendingMessageRef.current;
    
    isSendingMessageRef.current = false;

    if (previousContextKey === null) {
      restoreContextAttachments({ contextKey: currentContextKey });
      previousContextKeyRef.current = currentContextKey;
      return;
    }

    if (previousContextKey !== currentContextKey) {
      if (!wasSendingMessage) {
        saveContextAttachments({
          contextKey: previousContextKey,
          attachments: temporaryAttachments,
        });
      }
      
      restoreContextAttachments({ contextKey: currentContextKey });
      previousContextKeyRef.current = currentContextKey;
    }
  }, [activeView, currentConversationId]);

  return ( 
    <aiAssistantContext.Provider
      value={{
        isAssistantDragging,
        setIsAssistantDragging,
        isAssistantDisabled,
        isAssistantOpen,
        openAssistant,
        closeAssistant,
        assistantPosition,
        setAssistantPosition,
        draggableBounds,
        setDraggableBounds,
        isExtended,
        toggleExtended,
        setIsExtended,
        dialogContentRef,
        dialogFooterRef,
        lastUserMessageRef,
        dialogMessagesContainerRef,
        isUserPromptLimitReached,
        isPageLimitReached,
        isMessagesRateLimitActive,
        isAttachmentsRateLimitActive,
        isPromptLimitReached,
        promptLimitAlertVisible,
        setPromptLimitAlertVisible,
        showPromptLimitAlert,
        attachmentUploadError,
        setAttachmentUploadError,
        attachmentInfoMessage,
        setAttachmentInfoMessage,
        imageUploadError,
        setImageUploadError,
        documentDownloadAlertVisible,
        setDocumentDownloadAlertVisible,
        imageGenerationLimitReachedAlertVisible,
        setImageGenerationLimitReachedAlertVisible,
        isArtifactGenerationOnboardingAlertVisible,
        setIsArtifactGenerationOnboardingAlertVisible,
        wasFeedbackOpen,
        setWasFeedbackOpen,
        activeView,
        setActiveView,
        selectedTemplatePromptId,
        setSelectedTemplatePromptId,
        processNewConversation,
        stopResponse,
        continueConversation,
        regenerateResponse,
        resetActiveView,
        onboardingState,
        concludeOnboardingStep,
        currentSessionHints,
        addCurrentSessionHint: (hint: string) => setCurrentSessionHints(prev => new Set(prev).add(hint)),
        stagesShownInCurrentSession,
        aiFileUploadAbortControllerRef,
        displayMode,
        readyPodcastId,
        pendingPodcastDetails,
        clearPendingPodcastDetails,
        preparePodcastNavigation,
        pendingPodcastIdRef,
        isPodcastV1UxEnabled,
      }}
    >
      {children}
    </aiAssistantContext.Provider>
  );
};

export default AIAssistantProvider;
